import { useRef, useState } from "react"
import { Box, Text, Link } from "@chakra-ui/react"
import Form from "components/forms/Form"
import createAccountFormConfig from "components/registration/createAccountFormConfig"
import ProgressModal from "components/ui/ProgressModal"
import { getOktaUser, createOktaUser } from "services/internal"
import logger from "util/loggers/loggerBrowser"
import scrubPassword from "components/registration/scrubPassword"

export default function CreateAccountForm({ onSuccess, onError, locale, preview }) {
    const formRef = useRef()
    const [progressMessage, setProgressMessage] = useState("")

    // called after validation passes
    async function onSubmit(values) {
        let userId = null

        setProgressMessage("Creating your user...")

        // adds meeting slug to okta profile for tracking
        values.account_source = locale

        const response = await createOktaUser(values, preview)

        if (response.error) {
            logger.error("Error creating Okta user in CreateAccountForm", {
                error: response.error,
                submission: scrubPassword(values),
            })
            setProgressMessage("There was a problem with your registration. Please try again.")
            closeModal()
            return
        }
        userId = response.userId

        if (!userId) {
            // do nothing for now
            return
        }

        setProgressMessage("User created. Give us a moment to log you in...")

        let attempts = 0,
            maxAttempts = 20,
            checkUntilUserActive,
            featherParam = "from_new_account=true"

        // let's look for the Okta user status every 5 seconds until the user is ACTIVE or we reach the max attempts
        const findOktaUserAttempt = async () => {
            attempts++
            console.log("attempt: ", attempts)
            const userStatusCheck = await getOktaUser(userId, preview)

            if (userStatusCheck.user.status === "ACTIVE") {
                clearInterval(checkUntilUserActive)
                onSuccess(values)
            } else {
                // stop attempting
                if (attempts === maxAttempts) {
                    clearInterval(checkUntilUserActive)
                    setProgressMessage("")
                    logger.error(
                        `CreateAccountForm: account created but couldn't find the user after ${attempts} attempts`,
                        {
                            attemptsCount: attempts,
                            userId: userId,
                            submission: scrubPassword(values),
                        },
                    )
                    onError("We could not log you in. Please use the form to the login page.")
                }
            }
        }
        // wait 15 seconds (timeout + 1st interval) then check every 5 seconds
        setTimeout(() => {
            checkUntilUserActive = setInterval(findOktaUserAttempt, 5000)
        }, 10000)
    }

    const closeModal = (delay = 4000) => {
        setTimeout(() => {
            setProgressMessage("")
        }, delay)
    }

    // validate that the email is not in use in Okta
    const uniqueEmail = async (field, value) => {
        const result = await getOktaUser(value, preview)

        if (!result.error) {
            field.feedback = (
                <Box color="red.300">
                    <Text color="error" mt={0}>
                        This email is already in use!
                    </Text>
                    <Text mt={0}>
                        Please{" "}
                        <Link href="/login" textDecoration="underline" tabIndex="-1">
                            log in
                        </Link>{" "}
                        or{" "}
                        <Link
                            href="https://tctmd.okta.com/signin/forgot-password"
                            textDecoration="underline"
                            target="_blank"
                            tabIndex="-1"
                        >
                            reset your password
                        </Link>{" "}
                        if you have forgotten it.
                    </Text>
                </Box>
            )
            return false
        } else {
            field.feedback = null
            return true
        }
    }

    // TODO: there's something going on with Chakra's color vars
    // So I had to declare these styles to get form error colors working
    return (
        <Box
            css={{
                "& [aria-invalid=true]": {
                    borderColor: "#ee3d2c!important",
                    boxShadow: "0 0 0 1px #ee3d2c!important",
                },
                "& .chakra-field__errorText": {
                    color: "#ee3d2c",
                },
            }}
        >
            <Form config={createAccountFormConfig} onSubmit={onSubmit} validations={{ uniqueEmail }} ref={formRef} />
            <ProgressModal message={progressMessage} />
        </Box>
    )
}
